import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import type { ClientResponse } from '../../classes/ClientResponse';
import {
  extractClientErrorMessages,
  type ClientValidationErrorBag,
} from '../../types/system/api-responses.types';

@Component({
  selector: 'app-client-response-errors',
  imports: [],
  templateUrl: './client-response-errors.component.html',
  styleUrls: ['./client-response-errors.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientResponseErrorsComponent {
  errorTxt = input<string>('');
  clientResponse = input<ClientResponse | ClientValidationErrorBag | null>(null);

  normalized = computed(() => {
    const payload = this.extractValidationPayload();
    const validationErrors = payload?.validationErrors;
    const genericMessages = payload ? extractClientErrorMessages(payload) : [];
    return {
      errMsg: validationErrors?.errMsg ?? genericMessages,
      infoMsg: validationErrors?.infoMsg ?? [],
      dataErrors:
        this.normalizeDataErrors(payload?.dataErrors) ??
        (validationErrors?.errMsg?.length ? null : (genericMessages[0] ?? null)),
    };
  });

  isValidationResponse = computed(() => this.normalized().errMsg.length > 0);
  hasInfoMessage = computed(() => this.normalized().infoMsg.length > 0);
  hasDataErrors = computed(() => !!this.normalized().dataErrors?.length);

  private extractValidationPayload(): ClientValidationErrorBag | null {
    const response = this.clientResponse();
    if (!response) return null;
    if (this.isValidationPayload(response)) {
      return response;
    }
    if ('response' in response && this.isValidationPayload(response.response)) {
      return response.response;
    }
    return null;
  }

  private isValidationPayload(value: unknown): value is ClientValidationErrorBag {
    if (!value || typeof value !== 'object') return false;
    const record = value as Record<string, unknown>;
    return 'validationErrors' in record || 'dataErrors' in record;
  }

  private normalizeDataErrors(dataErrors?: string | null): string | null {
    if (typeof dataErrors !== 'string') {
      return null;
    }
    const trimmed = dataErrors.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
}
